'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import CopyToClipboard from 'react-copy-to-clipboard';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { isSupportAvailable } from '@/utils/supportHours';
import { BsTelephone } from 'react-icons/bs';
import Link from 'next/link';
import { buildTrackingId } from '@/utils/tracking-helper';
import Button from '../../../shared/Button';
import Checkmark from '../../../../../../public/images/checkmark-yellow-black-border.svg';
import CopyIcon from '../../../../../../public/images/copy-icon.svg';
import Alert from '../../../Alert/Alert';

/**
 * For now applicable only for this component so not exported (for readability sake)
 */
function CopyButton({ text }: { text: string }) {
  const t = useTranslations('CopyButton');
  const [copied, setCopied] = useState(false);

  return (
    <CopyToClipboard
      text={text}
      onCopy={() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <Button className="flex items-center gap-2 px-3 py-[6px]">
        <Image src={CopyIcon} alt={t('Copy')} width={20} height={20} aria-hidden />
        {copied ? t('copied') : t('copy')}
      </Button>
    </CopyToClipboard>
  );
}

function toTelHref(number: string) {
  return `tel:${number.replace(/[\s\-().]/g, '')}`;
}

type ConfirmationPagePhoneProps = {
  ticketId: number | null;
  hotlineAvailabilityNotice?: string;
  trackingPrefix?: string;
};

export default function ConfirmationPagePhone({
  ticketId,
  hotlineAvailabilityNotice,
  trackingPrefix,
}: Readonly<ConfirmationPagePhoneProps>) {
  const t = useTranslations('CSForm');
  const { country } = useLocalization();
  const isAvailable = isSupportAvailable(country);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 pb-20 pt-12 md:pt-16">
      {/* Header */}
      <div className="mb-10 flex flex-col items-center">
        <Image
          src={Checkmark}
          alt=""
          width={32}
          height={32}
          className="mb-8 h-14 w-14 sm:h-20 sm:w-20 md:h-14 md:w-14"
        />

        <h1 className="mb-3 text-2xl font-bold md:mb-6 md:text-4xl">
          {t('phoneConfirmationPageHeader')}
        </h1>

        <p className="whitespace-pre-line sm:text-lg">{t('phoneConfirmationPageInfoText')}</p>
      </div>

      {/* Ticket */}
      <div className="mb-8 w-full rounded-md border border-neutral-40 bg-gray-50 p-4">
        {/* Ticket.CopyHeader */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{t('yourTicketId')}</h2>

          <CopyButton text={String(ticketId)} />
        </div>

        {/* Ticket.Code */}
        <div className="mb-3 rounded-sm border border-gray-300 bg-white py-6 text-center text-xl font-bold">
          {`#${ticketId}`}
        </div>

        {/* Ticket.Alert — red when closed, yellow when open */}
        {isAvailable === false ? (
          <Alert className="flex rounded-sm border border-red-800 bg-red-800/10 text-red-800">
            <div className="mt-[0.25em]">
              <HiOutlineExclamationCircle className="mx-1 block h-4 w-4" />
            </div>
            <div>
              <span className="font-bold">{t('alertImportant')}</span> {t('alertClosedText')}
            </div>
          </Alert>
        ) : (
          <Alert type="warning-2" className="flex">
            <div className="mt-[0.25em]">
              <HiOutlineExclamationCircle className="mx-1 block h-4 w-4" />
            </div>
            <div>
              <span className="font-bold">{t('alertImportant')}</span> {t('alertText')}
            </div>
          </Alert>
        )}
      </div>

      {/* ContactHotline */}
      <div className="mb-8 w-full">
        <h2 className="mb-4 flex items-center text-lg font-bold">
          <BsTelephone className="mr-2 h-5 w-5" /> {t('contactOurHotline')}
        </h2>

        {country === 'us' ? (
          <ul>
            <li className="flex w-full justify-between border-b border-gray-200 py-3">
              <span className="flex-grow-2 mr-2">🇺🇸</span>
              <span className="flex-grow-0">{t('countryUnitedStates')}</span>
              <Link
                href={toTelHref(t('numberUnitedStates'))}
                className="flex-grow text-right font-semibold underline"
              >
                {t('numberUnitedStates')}
              </Link>
            </li>
          </ul>
        ) : (
          <ul>
            <li className="flex w-full justify-between border-b border-gray-200 py-3">
              <span className="flex-grow-2 mr-2">🌐</span>
              <span className="flex-grow-0">{t('countryInternational')}</span>
              <Link
                href={toTelHref(t('numberInternational'))}
                className="flex-grow text-right font-semibold underline"
              >
                {t('numberInternational')}
              </Link>
            </li>
            <li className="flex w-full justify-between border-b border-gray-200 py-3">
              <span className="flex-grow-2 mr-2">🇩🇪</span>
              <span className="flex-grow-0">{t('countryGermany')}</span>
              <Link
                href={toTelHref(t('numberGermany'))}
                className="flex-grow text-right font-semibold underline"
              >
                {t('numberGermany')}
              </Link>
            </li>
            <li className="flex w-full justify-between border-b border-gray-200 py-3">
              <span className="flex-grow-2 mr-2">🇦🇹</span>
              <span className="flex-grow-0">{t('countryAustria')}</span>
              <Link
                href={toTelHref(t('numberAustria'))}
                className="flex-grow text-right font-semibold underline"
              >
                {t('numberAustria')}
              </Link>
            </li>
            <li className="flex w-full justify-between border-b border-gray-200 py-3">
              <span className="flex-grow-2 mr-2">🇨🇭</span>
              <span className="flex-grow-0">{t('countrySwitzerland')}</span>
              <Link
                href={toTelHref(t('numberSwitzerland'))}
                className="flex-grow text-right font-semibold underline"
              >
                {t('numberSwitzerland')}
              </Link>
            </li>
            <li className="flex w-full justify-between py-3">
              <span className="flex-grow-2 mr-2">🇬🇧</span>
              <span className="flex-grow-0">{t('countryUnitedKingdom')}</span>
              <Link
                href={toTelHref(t('numberUnitedKingdom'))}
                className="flex-grow text-right font-semibold underline"
              >
                {t('numberUnitedKingdom')}
              </Link>
            </li>
          </ul>
        )}
      </div>

      {/* HotlineAvailability.ContactSchedule */}
      <div className="mb-2 w-full max-w-3xl rounded-md border border-neutral-40 bg-gray-50 p-4">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-lg font-bold">{t('hotlineAvailability')}</h2>
          {isAvailable ? (
            <span className="bg-green-800 rounded-md p-1 px-2 text-sm font-bold uppercase text-white">
              {t('hotlineOpenLabel')}
            </span>
          ) : (
            <span className="rounded-md bg-red-800 p-1 px-2 text-sm font-bold uppercase text-white">
              {t('hotlineClosedLabel')}
            </span>
          )}
        </div>

        {country === 'us' ? (
          <ul>
            <li className="py-2 sm:flex sm:justify-between">
              <span className="font-semibold">{t('hotlineUSAvailabilityDaysRowOne')} </span>
              <span>{t('hotlineUSAvailabilityTimeRowOne')}</span>
            </li>
          </ul>
        ) : (
          <ul>
            <li className="border-b border-gray-200 py-2 sm:flex sm:justify-between">
              <span className="font-semibold">{t('hotlineAvailabilityDaysRowOne')} </span>{' '}
              <span>{t('hotlineAvailabilityTimeRowOne')}</span>
            </li>
            <li className="py-2 sm:flex sm:justify-between">
              <span className="font-semibold">{t('hotlineAvailabilityDaysRowTwo')} </span>{' '}
              <span>{t('hotlineAvailabilityTimeRowTwo')}</span>
            </li>
          </ul>
        )}
      </div>
      {/* HotlineAvailability.Notice */}
      {hotlineAvailabilityNotice && (
        <p
          className="mb-10 px-5 text-sm text-gray-500"
          dangerouslySetInnerHTML={{ __html: hotlineAvailabilityNotice }}
        />
      )}
      <Alert type="success" className="mb-8 flex w-full">
        <div className="ml-1 mr-2 mt-[0.25em] ">
          <EnvelopeIcon className="h-4 w-4" />
        </div>
        <div className="flex-grow-0">{t('alertConfirmationEmailSent')}</div>
      </Alert>

      <button
        type="button"
        onClick={() => window.history.back()}
        className="text-sm font-semibold text-neutral-100 underline-offset-2 hover:underline"
        data-tile-element-id={buildTrackingId(trackingPrefix, 'phone-back')}
      >
        {'< '}
        {t('Back')}
      </button>
    </div>
  );
}
